<template>
  <!-- IDLE: quiet helper line under the form -->
  <p v-if="store.state === 'IDLE'" class="text-sm text-gray-500" data-testid="txn-analytics-state-idle">
    {{ t('messages.transactions.analytics.state.idle') }}
  </p>

  <!-- TOO_FEW: 0 < n < 5, quick actions widen the query -->
  <div
    v-else-if="store.state === 'TOO_FEW'"
    class="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 flex flex-col gap-3"
    data-testid="txn-analytics-state-too-few"
  >
    <div class="flex items-start gap-2">
      <span class="pi pi-info-circle text-amber-600 mt-0.5" />
      <p class="text-sm text-amber-900">{{ t('messages.transactions.analytics.state.too_few', { n: tooFewCount }) }}</p>
    </div>
    <div class="flex flex-wrap gap-2 justify-end">
      <Button
        size="small"
        severity="secondary"
        outlined
        icon="pi pi-calendar-plus"
        :label="t('messages.transactions.analytics.state.too_few_extend')"
        data-testid="txn-analytics-too-few-extend"
        @click="store.extendTwelveMonths()"
      />
      <Button
        size="small"
        severity="secondary"
        outlined
        icon="pi pi-map"
        :label="t('messages.transactions.analytics.state.too_few_county')"
        data-testid="txn-analytics-too-few-county"
        @click="store.wholeCounty()"
      />
    </div>
  </div>

  <!-- EMPTY: n = 0 -->
  <Message
    v-else-if="store.state === 'EMPTY'"
    severity="secondary"
    :closable="false"
    data-testid="txn-analytics-state-empty"
  >
    {{ t('messages.transactions.analytics.state.empty') }}
  </Message>

  <!-- TOO_MANY: pool-mode guard (> 200 000 rows) -->
  <Message
    v-else-if="store.state === 'TOO_MANY'"
    severity="warn"
    :closable="false"
    data-testid="txn-analytics-state-too-many"
  >
    {{ t('messages.transactions.analytics.state.too_many') }}
  </Message>

  <!-- ERROR: server failure, retry re-runs the same conditions -->
  <div
    v-else-if="store.state === 'ERROR'"
    class="rounded-md border border-red-200 bg-red-50 px-4 py-3 flex flex-wrap items-center justify-between gap-3"
    data-testid="txn-analytics-state-error"
  >
    <div class="flex items-start gap-2">
      <span class="pi pi-exclamation-triangle text-red-600 mt-0.5" />
      <p class="text-sm text-red-900">{{ t('messages.transactions.analytics.state.error') }}</p>
    </div>
    <Button
      size="small"
      severity="danger"
      outlined
      icon="pi pi-refresh"
      :label="t('messages.transactions.analytics.state.retry')"
      data-testid="txn-analytics-retry"
      @click="store.run()"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAnalyticsStore } from '@/stores/analyticsStore'

/**
 * RED-310 — the non-table result states (design-spec §6). Reads the store
 * directly: every quick action is a store action so the form/store thread
 * owns what "+12 kuud" and "Kogu maakond" actually change.
 */
const { t } = useI18n()
const store = useAnalyticsStore()

// TOO_FEW responses omit `bands`; n is still per period, so sum them for
// the multi-period case.
const tooFewCount = computed(() =>
  (store.result?.periods ?? []).reduce((sum, period) => sum + (period.n ?? 0), 0),
)
</script>
